import type {
    Battery,
    CalibrationMap,
    Drone,
    ModelPartsMap,
    ModelServiceIntervalMap,
    ServiceRecordMap
} from './types';

// OEM-recommended service intervals per aircraft model. Source of truth for
// reset-to-default and OEM-default display; the editable config is seeded from these.
export const MANUFACTURER_DEFAULTS: ModelServiceIntervalMap = {
    exedyV9: {
        battery: { unit: 'cycles', value: 300 },
        esc: { unit: 'hours', value: 1000 },
        flowMeter: { unit: 'hours', value: 200 },
        motor: { unit: 'hours', value: 100 },
        nozzleSet: { unit: 'hours', value: 100 },
        propSet: { unit: 'hours', value: 100 },
        pumpSealKit: { unit: 'hours', value: 100 },
        rtkAntenna: { unit: 'hours', value: 2000 }
    },
    exedyV12: {
        battery: { unit: 'cycles', value: 350 },
        esc: { unit: 'hours', value: 1200 },
        flowMeter: { unit: 'hours', value: 250 },
        motor: { unit: 'hours', value: 120 },
        nozzleSet: { unit: 'hours', value: 120 },
        propSet: { unit: 'hours', value: 120 },
        pumpSealKit: { unit: 'hours', value: 120 },
        rtkAntenna: { unit: 'hours', value: 2000 }
    }
};

// Which parts exist per model — drives the per-model config panel parts list.
// The V9 airframe ships without the inline flow meter the larger V12 carries, so the
// two models expose different part sets (7 vs 8) in the config panel.
export const MODEL_PARTS: ModelPartsMap = {
    exedyV9: ['motor', 'propSet', 'pumpSealKit', 'nozzleSet', 'battery', 'esc', 'rtkAntenna'],
    exedyV12: [
        'motor',
        'propSet',
        'pumpSealKit',
        'nozzleSet',
        'battery',
        'esc',
        'rtkAntenna',
        'flowMeter'
    ]
};

// Editable per-model config, seeded from the manufacturer defaults.
export const DEFAULT_SERVICE_INTERVALS: ModelServiceIntervalMap = {
    exedyV9: { ...MANUFACTURER_DEFAULTS.exedyV9 },
    exedyV12: { ...MANUFACTURER_DEFAULTS.exedyV12 }
};

const STANDARD_CALIBRATION: CalibrationMap = {
    flow: { daysAgo: 8, recorded: true },
    level: { daysAgo: 17, recorded: true },
    magnetometer: { daysAgo: 4, recorded: true },
    pump: { daysAgo: 12, recorded: true },
    weight: { daysAgo: 21, recorded: true }
};

const NEVER_CALIBRATED: CalibrationMap = {
    flow: { recorded: false },
    level: { daysAgo: 17, recorded: true },
    magnetometer: { daysAgo: 4, recorded: true },
    pump: { daysAgo: 12, recorded: true },
    weight: { recorded: false }
};

export const DRONES: Drone[] = [
    {
        calibration: STANDARD_CALIBRATION,
        faaRegistrationNumber: 'FA3X9K7M2P',
        faultHistory: [
            0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0
        ],
        firmware: {
            battery: 'notReported',
            esc: 'notReported',
            fc: 'compliant',
            pump: 'compliant'
        },
        firmwareVersion: 'v4.0.28',
        flightHours: 184,
        flights: 412,
        flowMeter: { flowRate: 6.1, usage: 96 },
        flownHectares: 1240.5,
        id: 'd01',
        lastLogSync: { label: '2m ago', stale: false },
        lifecycleStatus: 'enabled',
        model: 'exedyV9',
        motors: [
            { currentA: 15.1, tempC: 54, usage: 42 },
            { currentA: 14.8, tempC: 53, usage: 38 },
            { currentA: 14.9, tempC: 52, usage: 36 },
            { currentA: 15, tempC: 54, usage: 40 }
        ],
        name: 'Drone 01',
        pumps: [
            { flowKFactor: 1.01, flowRate: 6.1, usage: 28 },
            { flowKFactor: 1.02, flowRate: 6, usage: 24 }
        ],
        readiness: 'ready',
        serial: 'Exedy-9-2401-0012',
        status: 'good',
        topWarning: null,
        usage: { battery: 102, nozzleSet: 34, propSet: 61 },
        warranty: { end: '2026-09-14', start: '2024-09-14' },
        watching: []
    },
    {
        calibration: STANDARD_CALIBRATION,
        faultHistory: [
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0
        ],
        firmware: {
            battery: 'notReported',
            esc: 'notReported',
            fc: 'compliant',
            pump: 'compliant'
        },
        firmwareVersion: 'v4.0.28',
        flightHours: 92,
        flights: 206,
        flowMeter: { flowRate: 6, usage: 48 },
        flownHectares: 612.0,
        id: 'd02',
        lastLogSync: { label: '18m ago', stale: false },
        lifecycleStatus: 'released',
        model: 'exedyV9',
        motors: [
            { currentA: 15, tempC: 53, usage: 21 },
            { currentA: 14.7, tempC: 52, usage: 18 },
            { currentA: 14.8, tempC: 51, usage: 16 },
            { currentA: 14.9, tempC: 53, usage: 19 }
        ],
        name: 'Drone 02',
        pumps: [
            { flowKFactor: 1, flowRate: 6.1, usage: 14 },
            { flowKFactor: 1.01, flowRate: 6, usage: 11 }
        ],
        readiness: 'ready',
        serial: 'Exedy-9-2401-0019',
        status: 'good',
        topWarning: null,
        usage: { battery: 54, nozzleSet: 18, propSet: 33 },
        warranty: { end: '2027-02-02', start: '2025-02-02' },
        watching: []
    },
    {
        airworthyLog: { at: '2026-06-15T14:22:00Z', by: 'Jordan Vega', status: 'hold' },
        calibration: STANDARD_CALIBRATION,
        faaRegistrationNumber: 'FA7B2Q4N8R',
        faultHistory: [
            0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 2, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 1
        ],
        firmware: { battery: 'notReported', esc: 'notReported', fc: 'compliant', pump: 'outdated' },
        firmwareVersion: 'v4.0.26',
        flightHours: 312,
        flights: 698,
        flowMeter: { flowRate: 6.3, usage: 152 },
        flownHectares: 2105.3,
        id: 'd03',
        lastLogSync: { label: '3h ago', stale: false },
        lifecycleStatus: 'enabled',
        model: 'exedyV9',
        motors: [
            { currentA: 15.3, tempC: 56, usage: 71 },
            { currentA: 15, tempC: 55, usage: 64 },
            { currentA: 15.1, tempC: 54, usage: 60 },
            { currentA: 15.2, tempC: 56, usage: 67 }
        ],
        name: 'Drone 03',
        pumps: [
            { flowKFactor: 1.04, flowRate: 6.4, usage: 93 },
            { flowKFactor: 1.03, flowRate: 6.3, usage: 88 }
        ],
        readiness: 'hold',
        serial: 'Exedy-9-2401-0027',
        status: 'caution',
        topWarning: 'Pump seal service due in 4 days',
        usage: { battery: 186, nozzleSet: 62, propSet: 58 },
        warranty: { end: '2026-07-08', start: '2024-07-08' },
        watching: []
    },
    {
        airworthyLog: { at: '2026-06-18T09:05:00Z', by: 'Priya Anand', status: 'grounded' },
        calibration: STANDARD_CALIBRATION,
        faaRegistrationNumber: 'FA5T1W9J6L',
        faultHistory: [
            0, 0, 0, 1, 0, 0, 2, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 3, 0, 0, 1, 0, 0, 5, 2, 1, 4
        ],
        firmware: {
            battery: 'notReported',
            esc: 'notReported',
            fc: 'compliant',
            pump: 'compliant'
        },
        firmwareVersion: 'v4.0.26',
        flightHours: 208,
        flights: 467,
        flowMeter: { flowRate: 6.2, usage: 108 },
        flownHectares: 1402.7,
        id: 'd04',
        lastLogSync: { label: '2d ago', stale: true },
        lifecycleStatus: 'disabled',
        model: 'exedyV9',
        motors: [
            { currentA: 15.1, tempC: 54, usage: 55 },
            { currentA: 15, tempC: 53, usage: 49 },
            { currentA: 18.4, tempC: 72, usage: 110 },
            { currentA: 14.9, tempC: 53, usage: 47 }
        ],
        name: 'Drone 04',
        pumps: [
            { flowKFactor: 1.02, flowRate: 6.2, usage: 41 },
            { flowKFactor: 1.01, flowRate: 6.1, usage: 36 }
        ],
        readiness: 'grounded',
        serial: 'Exedy-9-2401-0037',
        status: 'critical',
        topWarning: 'Motor 3 ESC overtemp · last sortie',
        usage: { battery: 144, nozzleSet: 48, propSet: 62 },
        warranty: { end: '2026-05-30', start: '2024-05-30' },
        watching: ['Motor 2 temp trending +8°C over 5 sorties']
    },
    {
        calibration: STANDARD_CALIBRATION,
        faaRegistrationNumber: 'FA8M4C2V7D',
        faultHistory: [
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0
        ],
        firmware: {
            battery: 'notReported',
            esc: 'notReported',
            fc: 'compliant',
            pump: 'compliant'
        },
        firmwareVersion: 'v4.0.28',
        flightHours: 251,
        flights: 561,
        flowMeter: { flowRate: 6.2, usage: 168 },
        flownHectares: 1693.8,
        id: 'd05',
        lastLogSync: { label: '1h ago', stale: false },
        lifecycleStatus: 'enabled',
        model: 'exedyV9',
        motors: [
            { currentA: 15.2, tempC: 55, usage: 68 },
            { currentA: 15, tempC: 54, usage: 61 },
            { currentA: 15.1, tempC: 53, usage: 58 },
            { currentA: 15.1, tempC: 55, usage: 64 }
        ],
        name: 'Drone 05',
        pumps: [
            { flowKFactor: 1.06, flowRate: 6.5, usage: 52 },
            { flowKFactor: 1.01, flowRate: 6.1, usage: 47 }
        ],
        readiness: 'hold',
        serial: 'Exedy-9-2401-0044',
        status: 'caution',
        topWarning: null,
        usage: { battery: 174, nozzleSet: 58, propSet: 72 },
        warranty: { end: '2026-11-21', start: '2024-11-21' },
        watching: [
            'Pump 1 flow drift +6% over last 10 sorties',
            'Battery pack A voltage sag pattern'
        ]
    },
    {
        calibration: NEVER_CALIBRATED,
        faultHistory: Array(30).fill(0),
        firmware: {
            battery: 'notReported',
            esc: 'notReported',
            fc: 'compliant',
            pump: 'compliant'
        },
        firmwareVersion: 'v4.0.28',
        flightHours: 47,
        flights: 103,
        flowMeter: { flowRate: 6, usage: 23 },
        flownHectares: 318.4,
        id: 'd06',
        lastLogSync: { label: '6m ago', stale: false },
        lifecycleStatus: 'enabled',
        model: 'exedyV9',
        motors: [
            { currentA: 14.7, tempC: 51, usage: 11 },
            { currentA: 14.5, tempC: 50, usage: 8 },
            { currentA: 14.6, tempC: 50, usage: 7 },
            { currentA: 14.6, tempC: 51, usage: 10 }
        ],
        name: 'Drone 06',
        pumps: [
            { flowKFactor: 1, flowRate: 6, usage: 8 },
            { flowKFactor: 1, flowRate: 6, usage: 6 }
        ],
        readiness: 'ready',
        serial: 'Exedy-9-2401-0051',
        status: 'good',
        topWarning: null,
        usage: { battery: 27, nozzleSet: 9, propSet: 19 },
        warranty: { end: '2027-12-05', start: '2025-12-05' },
        watching: []
    },
    {
        calibration: STANDARD_CALIBRATION,
        faultHistory: [
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0
        ],
        firmware: {
            battery: 'notReported',
            esc: 'notReported',
            fc: 'compliant',
            pump: 'compliant'
        },
        firmwareVersion: 'v4.0.28',
        flightHours: 133,
        flights: 298,
        flowMeter: { flowRate: 6.1, usage: 70 },
        flownHectares: 894.6,
        id: 'd07',
        lastLogSync: { label: '9h ago', stale: false },
        lifecycleLog: { at: '2026-07-03T15:12:00Z', by: 'Dana Whitfield', status: 'claimed' },
        lifecycleStatus: 'claimed',
        model: 'exedyV12',
        motors: [
            { currentA: 14.9, tempC: 53, usage: 31 },
            { currentA: 14.7, tempC: 52, usage: 27 },
            { currentA: 14.8, tempC: 51, usage: 25 },
            { currentA: 14.8, tempC: 52, usage: 29 },
            { currentA: 14.9, tempC: 53, usage: 30 },
            { currentA: 14.8, tempC: 52, usage: 26 }
        ],
        name: 'Drone 07',
        pumps: [
            { flowKFactor: 1.01, flowRate: 6.1, usage: 21 },
            { flowKFactor: 1, flowRate: 6, usage: 18 },
            { flowKFactor: 1.01, flowRate: 6.1, usage: 19 }
        ],
        readiness: 'ready',
        serial: 'Exedy-9-2401-0058',
        status: 'good',
        topWarning: null,
        usage: { battery: 81, nozzleSet: 27, propSet: 44 },
        warranty: { end: '2027-04-18', start: '2025-04-18' },
        watching: []
    },
    {
        calibration: STANDARD_CALIBRATION,
        faultHistory: [
            0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0
        ],
        firmware: {
            battery: 'notReported',
            esc: 'notReported',
            fc: 'compliant',
            pump: 'compliant'
        },
        firmwareVersion: 'v4.0.28',
        flightHours: 166,
        flights: 372,
        flowMeter: { flowRate: 6.1, usage: 86 },
        flownHectares: 1118.2,
        id: 'd08',
        lastLogSync: { label: '5d ago', stale: true },
        lifecycleStatus: 'enabled',
        model: 'exedyV12',
        motors: [
            { currentA: 15, tempC: 53, usage: 38 },
            { currentA: 14.8, tempC: 52, usage: 33 },
            { currentA: 14.9, tempC: 51, usage: 30 },
            { currentA: 14.9, tempC: 53, usage: 35 },
            { currentA: 15, tempC: 53, usage: 36 },
            { currentA: 14.8, tempC: 52, usage: 32 }
        ],
        name: 'Drone 08',
        pumps: [
            { flowKFactor: 1.01, flowRate: 6.1, usage: 24 },
            { flowKFactor: 1, flowRate: 6, usage: 20 },
            { flowKFactor: 1.01, flowRate: 6.1, usage: 22 }
        ],
        readiness: 'ready',
        serial: 'Exedy-9-2401-0063',
        status: 'good',
        topWarning: null,
        usage: { battery: 93, nozzleSet: 31, propSet: 51 },
        warranty: { end: '2027-01-09', start: '2025-01-09' },
        watching: []
    }
];

// Pooled battery packs shared across the fleet (10 packs / 8 drones). Cycle counts
// are measured against the configured battery cycle interval to derive service life.
export const BATTERIES: Battery[] = [
    {
        cycleCount: 84,
        droneIds: ['d01', 'd02'],
        electricals: { reported: false },
        firmwareHistory: [],
        firmwareVersion: 'BMS v2.4.1',
        id: 'b01',
        lastInstalledDroneId: 'd01',
        lastSync: { label: '2m ago', stale: false },
        name: 'Battery 01',
        serialNumber: 'EXB-2401-0007',
        status: 'ok'
    },
    {
        cycleCount: 142,
        droneIds: ['d01', 'd03'],
        electricals: { reported: false },
        firmwareHistory: [
            {
                at: '2026-02-14T09:20:00.000Z',
                by: 'Dana Whitfield',
                from: 'BMS v2.3.5',
                to: 'BMS v2.4.0'
            }
        ],
        firmwareVersion: 'BMS v2.4.0',
        id: 'b02',
        lastInstalledDroneId: 'd03',
        lastSync: { label: '3h ago', stale: false },
        name: 'Battery 02',
        serialNumber: 'EXB-2401-0011',
        status: 'ok'
    },
    {
        cycleCount: 271,
        droneIds: ['d03', 'd05'],
        electricals: { reported: false },
        firmwareHistory: [
            {
                at: '2026-01-08T15:45:00.000Z',
                by: 'Marcus Lindgren',
                from: 'BMS v2.3.5',
                to: 'BMS v2.4.0'
            },
            {
                at: '2026-03-22T11:10:00.000Z',
                by: 'Dana Whitfield',
                from: 'BMS v2.4.0',
                to: 'BMS v2.4.1'
            }
        ],
        firmwareVersion: 'BMS v2.4.1',
        id: 'b03',
        lastInstalledDroneId: 'd05',
        lastSync: { label: '1h ago', stale: false },
        name: 'Battery 03',
        serialNumber: 'EXB-2401-0014',
        status: 'aging'
    },
    {
        cycleCount: 198,
        droneIds: ['d02', 'd04'],
        electricals: { reported: false },
        firmwareHistory: [],
        firmwareVersion: 'BMS v2.4.0',
        id: 'b04',
        lastInstalledDroneId: 'd04',
        lastSync: { label: '2d ago', stale: true },
        name: 'Battery 04',
        serialNumber: 'EXB-2401-0019',
        status: 'ok'
    },
    {
        cycleCount: 256,
        droneIds: ['d04', 'd05'],
        electricals: { reported: false },
        firmwareHistory: [
            {
                at: '2026-02-28T08:05:00.000Z',
                by: 'Marcus Lindgren',
                from: 'BMS v2.4.0',
                to: 'BMS v2.4.1'
            }
        ],
        firmwareVersion: 'BMS v2.4.1',
        id: 'b05',
        lastInstalledDroneId: 'd05',
        lastSync: { label: '1h ago', stale: false },
        name: 'Battery 05',
        serialNumber: 'EXB-2401-0023',
        status: 'aging'
    },
    {
        cycleCount: 61,
        droneIds: ['d06'],
        electricals: { reported: false },
        firmwareHistory: [],
        firmwareVersion: 'BMS v2.4.1',
        id: 'b06',
        lastInstalledDroneId: 'd06',
        lastSync: { label: '6m ago', stale: false },
        name: 'Battery 06',
        serialNumber: 'EXB-2401-0028',
        status: 'ok'
    },
    {
        cycleCount: 117,
        droneIds: ['d06', 'd07'],
        electricals: { reported: false },
        firmwareHistory: [],
        firmwareVersion: 'BMS v2.4.0',
        id: 'b07',
        lastInstalledDroneId: 'd07',
        lastSync: { label: '9h ago', stale: false },
        name: 'Battery 07',
        serialNumber: 'EXB-2401-0031',
        status: 'ok'
    },
    {
        cycleCount: 303,
        droneIds: ['d07', 'd08'],
        electricals: { reported: false },
        firmwareHistory: [
            {
                at: '2025-11-30T13:00:00.000Z',
                by: 'Priya Raman',
                from: 'BMS v2.3.5',
                to: 'BMS v2.4.0'
            }
        ],
        firmwareVersion: 'BMS v2.4.0',
        id: 'b08',
        lastInstalledDroneId: 'd08',
        lastSync: { label: '5d ago', stale: true },
        name: 'Battery 08',
        serialNumber: 'EXB-2401-0035',
        status: 'aging'
    },
    {
        cycleCount: 165,
        droneIds: ['d08', 'd01'],
        electricals: { reported: false },
        firmwareHistory: [],
        firmwareVersion: 'BMS v2.4.1',
        id: 'b09',
        lastInstalledDroneId: 'd01',
        lastSync: { label: '18m ago', stale: false },
        name: 'Battery 09',
        serialNumber: 'EXB-2401-0040',
        status: 'ok'
    },
    {
        cycleCount: 12,
        droneIds: [],
        electricals: { reported: false },
        firmwareHistory: [],
        firmwareVersion: 'BMS v2.3.5',
        id: 'b10',
        lastInstalledDroneId: null,
        lastSync: { label: '4d ago', stale: true },
        name: 'Battery 10',
        serialNumber: 'EXB-2401-0044',
        status: 'ok'
    }
];

// Seeded service log per drone. usageAtRecord snapshots the counter reading at the
// time of service; the reset derivation subtracts only the latest active snapshot
// (see getEffectiveUsage). Voided records stay in the log but no longer apply.
export const SEEDED_SERVICE_RECORDS: ServiceRecordMap = {
    d01: [
        {
            at: '2026-06-28T16:40:00.000Z',
            by: 'Jordan Vega',
            eventType: 'inspected',
            id: 'sr-d01-01',
            notes: 'Pre-season airframe inspection — no findings.',
            performedAt: '2026-06-28T16:10:00.000Z',
            usageAtRecord: 0,
            voided: true,
            voidedAt: '2026-06-30T09:15:00.000Z',
            voidedBy: 'Dana Whitfield'
        }
    ],
    d02: [
        // SR-24 — per-prop replacement: only the prop at motor position 2 resets
        // (raw propSet 33 − snapshot 20 = 13); positions 1/3/4 stay at the shared
        // aggregate reading (33), so the Props ring keeps showing the worst prop.
        {
            at: '2026-07-14T09:45:00.000Z',
            by: 'Jordan Vega',
            eventType: 'replaced',
            id: 'sr-d02-01',
            notes: 'Prop swapped after leading-edge chip found on walkaround.',
            partSerialNumber: 'EXP-2503-0088',
            partType: 'propSet',
            performedAt: '2026-07-14T09:30:00.000Z',
            unitNumber: 2,
            usageAtRecord: 20
        }
    ],
    d03: [
        // Seeded edit history: Marcus amended his notes the next morning, so the
        // stored `notes` matches the newValue of the latest changeLog entry.
        {
            at: '2026-07-10T15:55:00.000Z',
            by: 'Marcus Lindgren',
            changeLog: [
                {
                    changedAt: '2026-07-11T08:20:00.000Z',
                    changedBy: 'Marcus Lindgren',
                    fieldChanged: 'notes',
                    newValue: 'Seal kit flushed and re-torqued after flow drift.',
                    oldValue: 'Seal kit flushed after flow drift.'
                }
            ],
            eventType: 'serviced',
            id: 'sr-d03-01',
            notes: 'Seal kit flushed and re-torqued after flow drift.',
            partSerialNumber: 'EXS-2410-0114',
            partType: 'pumpSealKit',
            performedAt: '2026-07-10T15:30:00.000Z',
            unitNumber: 1,
            usageAtRecord: 60
        }
    ],
    d04: [
        {
            at: '2026-07-18T14:05:00.000Z',
            by: 'Dana Whitfield',
            eventType: 'replaced',
            id: 'sr-d04-01',
            notes: 'Motor 3 swapped after ESC overtemp fault.',
            partSerialNumber: 'EXM-2502-0067',
            partType: 'motor',
            performedAt: '2026-07-18T13:45:00.000Z',
            unitNumber: 3,
            usageAtRecord: 110
        }
    ],
    d05: [
        {
            at: '2026-07-08T11:35:00.000Z',
            by: 'Jordan Vega',
            eventType: 'replaced',
            id: 'sr-d05-01',
            partType: 'propSet',
            performedAt: '2026-07-08T11:20:00.000Z',
            usageAtRecord: 60
        }
    ],
    d06: [
        {
            at: '2026-07-19T10:20:00.000Z',
            by: 'Priya Anand',
            calibrationKind: 'flow',
            eventType: 'calibrated',
            id: 'sr-d06-01',
            performedAt: '2026-07-19T10:00:00.000Z',
            usageAtRecord: 0
        }
    ],
    d08: [
        // Part-targeted inspection: identifies the unit inspected and the serial
        // verified on it, but never touches a counter (inspections stamp 0).
        {
            at: '2026-07-21T13:25:00.000Z',
            by: 'Marcus Lindgren',
            eventType: 'inspected',
            id: 'sr-d08-01',
            notes: 'Motor 2 bearing check after vibration report — serial verified.',
            partSerialNumber: 'EXM-2504-0102',
            partType: 'motor',
            performedAt: '2026-07-21T13:05:00.000Z',
            unitNumber: 2,
            usageAtRecord: 0
        }
    ]
};
